import React, { useCallback, Fragment } from 'react';
import { useField } from 'formik';
import { SelectOption, SelectTWProps, SelectValue } from '../interfaces';
import { useErrorText } from '../../hooks/useErrorText';
import { Combobox, Transition } from '@headlessui/react';
import { mergeTests } from '../../../ui-toolkit/test-utilities/mergeTests';
import { ComboboxErrorHintLabel } from './components/ComboboxErrorHintLabel';
import classNames from 'classnames';
import { MobilityHeaderLabelText } from '../../formCellTW/MobilityHeaderLabelText';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Show } from '../../../components/shared/Show';
import type { FCWithChildren } from '../../../types/FCWithChildren';

interface MobilityHeaderSelectFieldProps extends SelectTWProps {
  name: string;
  isInlineLabel?: boolean;
  optionClassName?: string;
  buttonClassName?: string;
  labelClassName?: string;
}

export const MobilityHeaderSelectField: React.FC<MobilityHeaderSelectFieldProps> = ({ name, ...rest }) => {
  const [fieldInputProps, fieldMetaProps, fieldHelperProps] = useField<SelectValue>({ name });

  const errorText = useErrorText<SelectValue>(fieldMetaProps);

  const onChange = useCallback(
    (option: SelectOption) => {
      fieldHelperProps.setTouched(true);
      fieldHelperProps.setValue(option.value);
    },
    [fieldHelperProps]
  );

  const listboxValue: SelectOption | undefined = rest.options.find(
    (option: SelectOption) => option.value === fieldInputProps.value
  );

  return (
    <MobilityHeaderListbox
      errorText={errorText}
      {...rest}
      {...fieldInputProps}
      onChange={onChange}
      value={listboxValue}
    />
  );
};

export interface MobilityHeaderListboxProps extends SelectTWProps {
  value?: SelectOption | null;
  onChange?: (option: SelectOption) => void;
  isInlineLabel?: boolean;
  optionClassName?: string;
  buttonClassName?: string;
  labelClassName?: string;
}

/**
 * This is a controlled Listbox. An adapter controls its value and onChange.
 * https://headlessui.com/react/listbox
 * */
export const MobilityHeaderListbox: React.FC<MobilityHeaderListboxProps> = ({
  labelText,
  errorText,
  hintText,
  isRequired,
  disabled,
  options,
  placeholder,
  onChange,
  value,
  className,
  ...rest
}) => {
  return (
    <Combobox
      value={value}
      onChange={onChange}
      disabled={disabled}
      as={'div'}
      data-test-id={mergeTests('comboBox', rest['data-test-id'])}
      className={className}
    >
      {({ open }) => (
        <ComboboxFormCell
          errorText={errorText}
          hintText={hintText}
          labelText={labelText}
          isRequired={isRequired}
          isInlineLabel={rest.isInlineLabel}
          labelClassName={rest.labelClassName}
          name={rest.name}
        >
          <div className="relative">
            <ComboboxButton className={rest.buttonClassName}>
              <span className="block truncate">{value?.label ?? placeholder ?? 'Select...'}</span>
            </ComboboxButton>
            <ComboboxOptions optionClassName={rest.optionClassName} options={options} open={open} />
          </div>
        </ComboboxFormCell>
      )}
    </Combobox>
  );
};

export const ComboboxFormCell: React.FC<{
  labelText?: string;
  errorText?: string;
  hintText?: string;
  isRequired?: boolean;
  isInlineLabel?: boolean;
  labelClassName?: string;
  name?: string;
  children?: React.ReactNode;
}> = ({ children, labelText, hintText, errorText, isRequired, isInlineLabel, name, labelClassName }) => {
  const text = `${labelText}${isRequired ? ' *' : ''}`;
  if (isInlineLabel) {
    return (
      <div>
        <div className={'flex gap-1'}>
          <MobilityHeaderLabelText
            labelText={labelText}
            isError={!!errorText}
            isRequired={isRequired}
            name={name}
            className={labelClassName}
          />
          {children}
        </div>
        <ComboboxErrorHintLabel errorText={errorText} hintText={hintText} />
      </div>
    );
  }
  return (
    <div>
      <Combobox.Label
        className={classNames('block whitespace-pre-line', {
          'text-text-error': !!errorText,
          'text-text-default-content': !errorText,
        })}
      >
        {text}
      </Combobox.Label>
      {children}
      <ComboboxErrorHintLabel errorText={errorText} hintText={hintText} />
    </div>
  );
};

export const ComboboxButton: FCWithChildren<{ className?: string }> = ({ children, className }) => (
  <Combobox.Button
    className={classNames(
      className,
      'relative w-full cursor-default rounded-md bg-white text-left text-text-default-content shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-foundation-primary'
    )}
  >
    {children}
    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
    </span>
  </Combobox.Button>
);

export const ComboboxOptions: React.FC<{ open: boolean; options: SelectOption[]; optionClassName?: string }> = ({
  open,
  options,
  optionClassName,
}) => {
  return (
    <Transition show={open} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
      <Combobox.Options className="absolute z-10 max-h-60 overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        {options.map((option) => (
          <Combobox.Option
            key={option.value + ''}
            className={({ active }) =>
              classNames(
                optionClassName,
                {
                  'bg-foundation-primary text-white': active,
                  'text-text-default-content': !active,
                },
                'relative cursor-default select-none'
              )
            }
            value={option}
          >
            {({ selected, active }) => (
              <Fragment>
                <span
                  className={classNames('block truncate', {
                    'font-semibold': selected,
                    'font-normal': !selected,
                  })}
                >
                  {option.label}
                </span>
                <Show when={selected}>
                  <span
                    className={classNames(
                      {
                        'text-white': active,
                        'text-foundation-primary': !active,
                      },
                      'absolute inset-y-0 right-0 flex items-center pr-4'
                    )}
                  >
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                </Show>
              </Fragment>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Transition>
  );
};
